import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Input,
  Textarea,
  Button,
  Spinner,
} from '@material-tailwind/react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';

function CoverSection({ content, draft, onSave, onReset, isSaving }) {
  console.log('[CoverSection] content:', content);
  const [editedReportTitle, setEditedReportTitle] = useState('');
  const [editedSubtitle, setEditedSubtitle] = useState('');
  const [editedAircraftDescription, setEditedAircraftDescription] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [editedCustomerName, setEditedCustomerName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Build effective content by merging section content with draft-level fields
  const effectiveContent = useMemo(() => ({
    reportTitle: content?.reportTitle || 'Market Valuation',
    subtitle: content?.subtitle || 'Brokerage Services',
    aircraftDescription: content?.aircraftDescription || '',
    date: content?.date || '',
    customerName: content?.customerName || draft?.customerName || '',
    // Read-only field from draft
    registration: draft?.subjectAircraft?.registration || draft?.aircraftId || '',
  }), [content, draft]);

  // Initialize form from effective content
  useEffect(() => {
    setEditedReportTitle(effectiveContent.reportTitle);
    setEditedSubtitle(effectiveContent.subtitle);
    setEditedAircraftDescription(effectiveContent.aircraftDescription);
    setEditedDate(effectiveContent.date);
    setEditedCustomerName(effectiveContent.customerName);
    setHasChanges(false);
  }, [effectiveContent]);

  // Check for changes
  useEffect(() => {
    const changed =
      editedReportTitle !== effectiveContent.reportTitle ||
      editedSubtitle !== effectiveContent.subtitle ||
      editedAircraftDescription !== effectiveContent.aircraftDescription ||
      editedDate !== effectiveContent.date ||
      editedCustomerName !== effectiveContent.customerName;
    setHasChanges(changed);
  }, [editedReportTitle, editedSubtitle, editedAircraftDescription, editedDate, editedCustomerName, effectiveContent]);

  const handleSave = () => {
    if (onSave) {
      onSave({
        reportTitle: editedReportTitle,
        subtitle: editedSubtitle,
        aircraftDescription: editedAircraftDescription,
        date: editedDate,
        customerName: editedCustomerName,
      });
    }
  };

  const handleReset = () => {
    setEditedReportTitle(effectiveContent.reportTitle);
    setEditedSubtitle(effectiveContent.subtitle);
    setEditedAircraftDescription(effectiveContent.aircraftDescription);
    setEditedDate(effectiveContent.date);
    setEditedCustomerName(effectiveContent.customerName);
    setHasChanges(false);
    if (onReset) {
      onReset();
    }
  };

  if (!content && !draft) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No cover page data available</Typography>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      <Card className="border border-blue-200">
        <CardBody className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-blue-500" />
              <Typography variant="small" className="font-medium text-blue-800">
                Cover Details
              </Typography>
            </div>
            {hasChanges && (
              <span className="text-xs text-amber-600 font-medium">Modified</span>
            )}
          </div>

          <div className="space-y-4">
            {/* Read-only field */}
            {effectiveContent.registration && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <Typography variant="small" className="text-gray-500 text-xs mb-1">Registration</Typography>
                <Typography variant="small" className="font-medium font-mono">{effectiveContent.registration}</Typography>
              </div>
            )}

            <Input
              label="Report Title"
              value={editedReportTitle}
              onChange={(e) => setEditedReportTitle(e.target.value)}
              size="md"
            />

            <Input
              label="Subtitle"
              value={editedSubtitle}
              onChange={(e) => setEditedSubtitle(e.target.value)}
              size="md"
            />

            <Input
              label="Customer Name"
              value={editedCustomerName}
              onChange={(e) => setEditedCustomerName(e.target.value)}
              size="md"
            />

            <Textarea
              label="Aircraft Description"
              value={editedAircraftDescription}
              onChange={(e) => setEditedAircraftDescription(e.target.value)}
              rows={2}
            />

            <Input
              label="Date"
              value={editedDate}
              onChange={(e) => setEditedDate(e.target.value)}
              size="md"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outlined"
              size="sm"
              onClick={handleReset}
              disabled={!hasChanges || isSaving}
              className="py-1 px-3 text-xs"
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="py-1 px-3 text-xs"
            >
              {isSaving ? (
                <>
                  <Spinner className="h-3 w-3 mr-1" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export default CoverSection;
